/**
 * Shared product quick view.
 *
 * Any element with [data-product-quick-view] opens the single dialog hosted
 * by snippets/product-quick-view-host.liquid. Product markup is fetched from
 * the Section Rendering API (sections/section-rendering-product-quick-view.liquid)
 * and is not rendered until the click.
 *
 * Optional attributes:
 *   data-product-handle  — product handle (falls back to /products/{handle} in href)
 *   data-variant-id      — variant id (falls back to ?variant= on href)
 */

import './quick-view-multi-option.js';
import { activateQuickViewDialog } from './shop-the-look-quick-view.js';

const SECTION_ID = 'section-rendering-product-quick-view';
const HOST_ID = 'product-quick-view-host';

/** @type {Map<string, string>} */
const htmlCache = new Map();

/** @type {AbortController | null} */
let activeController = null;

/** Ignores a response that belongs to an older click. */
let openToken = 0;

/** Keeps the page scroll lock when the status dialog closes into the product dialog. */
let retainScrollLock = false;

/**
 * @param {Event} event
 */
function onClick(event) {
  if (!(event.target instanceof Element)) return;
  if (event.defaultPrevented) return;
  if ('button' in event && event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const trigger = event.target.closest('[data-product-quick-view]');
  if (!(trigger instanceof HTMLElement)) return;

  const interactive = event.target.closest('button, input, select, textarea, label, a');
  if (interactive instanceof HTMLElement && interactive !== trigger && trigger.contains(interactive)) return;

  const host = document.getElementById(HOST_ID);
  if (!(host instanceof HTMLElement)) return;

  const { handle, variantId } = readTarget(trigger);
  if (!handle) return;

  event.preventDefault();
  openQuickView(host, handle, variantId);
}

/**
 * @param {HTMLElement} trigger
 * @returns {{ handle: string, variantId: string }}
 */
function readTarget(trigger) {
  const href = trigger.getAttribute('href') || '';
  let handle = trigger.dataset.productHandle || '';
  let variantId = trigger.dataset.variantId || '';

  if (!href) return { handle, variantId };

  try {
    const url = new URL(href, window.location.origin);
    if (!handle) {
      const match = url.pathname.match(/\/products\/([^/]+)/);
      if (match?.[1]) handle = decodeURIComponent(match[1]);
    }
    if (!variantId) variantId = url.searchParams.get('variant') || '';
  } catch {
    // Keep the data-attribute values when href is not a URL.
  }

  return { handle, variantId };
}

/**
 * @param {HTMLElement} host
 * @param {string} handle
 * @param {string} variantId
 */
async function openQuickView(host, handle, variantId) {
  const token = ++openToken;
  activeController?.abort();
  const controller = new AbortController();
  activeController = controller;

  const cacheKey = `${handle}:${variantId}`;
  const cached = htmlCache.get(cacheKey);
  if (cached) {
    const dialog = parseDialog(cached);
    if (dialog instanceof HTMLDialogElement) {
      mountDialog(host, dialog);
      return;
    }
    htmlCache.delete(cacheKey);
  }

  showStatus(host, 'loading');

  try {
    const html = await loadSectionHtml(handle, variantId, controller.signal);
    if (token !== openToken) return;

    const dialog = parseDialog(html);
    if (!(dialog instanceof HTMLDialogElement)) {
      showStatus(host, 'error');
      return;
    }

    mountDialog(host, dialog);
  } catch (error) {
    if (isAbortError(error) || token !== openToken) return;
    showStatus(host, 'error');
  }
}

/**
 * @param {string} handle
 * @param {string} variantId
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
async function loadSectionHtml(handle, variantId, signal) {
  const cacheKey = `${handle}:${variantId}`;
  const cached = htmlCache.get(cacheKey);
  if (cached) return cached;

  const url = new URL(`/products/${encodeURIComponent(handle)}`, window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);
  if (variantId) url.searchParams.set('variant', variantId);

  const response = await fetch(url.pathname + url.search, {
    signal,
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  });

  if (!response.ok) throw new Error(`Quick view section failed (${response.status})`);

  const html = await response.text();
  if (!html.includes('stl-qv-dialog')) throw new Error('Quick view dialog missing');

  htmlCache.set(cacheKey, html);
  return html;
}

/**
 * @param {string} html
 * @returns {HTMLDialogElement | null}
 */
function parseDialog(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const dialog = doc.querySelector('dialog.stl-qv-dialog');
  if (!(dialog instanceof HTMLDialogElement)) return null;
  return /** @type {HTMLDialogElement} */ (document.importNode(dialog, true));
}

/**
 * @param {HTMLElement} host
 * @param {HTMLDialogElement} dialog
 */
function mountDialog(host, dialog) {
  host.querySelectorAll('dialog.stl-qv-dialog:not([data-product-quick-view-status])').forEach((node) => {
    if (node instanceof HTMLDialogElement && node.open) node.close();
    node.remove();
  });

  const status = getStatusDialog(host);
  retainScrollLock = true;
  if (status?.open) status.close();

  host.appendChild(dialog);
  activateQuickViewDialog(dialog);
}

/**
 * @param {HTMLElement} host
 * @param {'loading' | 'error'} mode
 */
function showStatus(host, mode) {
  host.querySelectorAll('dialog.stl-qv-dialog:not([data-product-quick-view-status])').forEach((node) => {
    if (node instanceof HTMLDialogElement && node.open) node.close();
    node.remove();
  });

  const status = getStatusDialog(host);
  if (!(status instanceof HTMLDialogElement)) return;

  const text = status.querySelector('[data-product-quick-view-status-text]');
  const spinner = status.querySelector('[data-product-quick-view-spinner]');
  const isError = mode === 'error';

  if (text) {
    text.textContent = isError
      ? status.dataset.errorText || 'Could not open this product. Please try again.'
      : status.dataset.loadingText || 'Loading...';
  }
  if (spinner instanceof HTMLElement) spinner.hidden = isError;

  status.setAttribute('aria-busy', isError ? 'false' : 'true');
  if (!status.open) status.showModal();
  document.body.style.overflow = 'hidden';
}

/**
 * @param {HTMLElement} host
 * @returns {HTMLDialogElement | null}
 */
function getStatusDialog(host) {
  const status = host.querySelector('[data-product-quick-view-status]');
  return status instanceof HTMLDialogElement ? status : null;
}

/**
 * @param {HTMLElement} host
 */
function bindStatusDialog(host) {
  const status = getStatusDialog(host);
  if (!(status instanceof HTMLDialogElement) || status.dataset.productQuickViewBound === 'true') return;
  status.dataset.productQuickViewBound = 'true';

  status.querySelector('[data-product-quick-view-close]')?.addEventListener('click', () => status.close());

  status.addEventListener('click', (event) => {
    if (event.target === status) status.close();
  });

  status.addEventListener('close', () => {
    if (retainScrollLock) {
      retainScrollLock = false;
      return;
    }
    document.body.style.overflow = '';
  });
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isAbortError(error) {
  return error instanceof DOMException && error.name === 'AbortError';
}

const host = document.getElementById(HOST_ID);
if (host instanceof HTMLElement) {
  bindStatusDialog(host);
  document.addEventListener('click', onClick, true);
}
